import { useAuth } from '@agentnet/auth';
import { getMarketPlaceProducts, toggleMarketPlaceProducts } from 'api/admin/integrations-features-api';
import { MarketplaceProduct, MarketplaceProductStatus } from 'api/admin/types';
import {
  ContactValidationResult,
  IdentityVerificationResult,
  ISavePhoneNumberPayload,
} from 'api/file/interfaces/get-file-order-insights';
import { validateContactInfo, verifyIdentity } from 'api/orderInsights/order-insights-api';
import { useContext, useEffect } from 'react';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import { ProfileContext, ProfileContextInterface } from './ProfileContext';
import useAsync from './useAsync';

export const useMarketPlace = () => {
  const { addGlobalMsg } = useGlobalMessages();
  const { getAccessToken } = useAuth();
  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { userFirm } = profileCtx;

  const isMarketPlaceEnabled = true;

  const services = async (): Promise<MarketplaceProduct[]> => {
    if (!isMarketPlaceEnabled) {
      return [];
    }
    const token = await getAccessToken();
    const firmId = userFirm?.firmId;
    return await getMarketPlaceProducts(firmId, token);
  };

  const {
    execute: fetchMarketPlaceServices,
    status: fetchMarketPlaceStatus,
    value: fetchMarketPlaceServicesResults,
    errors: fetchMarketPlaceErrors,
  } = useAsync<MarketplaceProduct[]>(services, false);

  const updateService = async (productStatus: MarketplaceProductStatus): Promise<MarketplaceProductStatus> => {
    const token = await getAccessToken();
    const firmId = userFirm?.firmId;
    return await toggleMarketPlaceProducts({ ...productStatus, firmId }, token);
  };

  const {
    execute: updateMarketPlaceServiceStatus,
    status: updateServiceStatus,
    value: updateServiceStatusResults,
    errors: updateServiceStatusErrors,
  } = useAsync<MarketplaceProductStatus>(updateService, false);

  const validateContactDetails = async (partyInfo: ISavePhoneNumberPayload): Promise<ContactValidationResult> => {
    const token = await getAccessToken();
    return await validateContactInfo({ ...partyInfo }, token);
  };

  const {
    execute: doValidateContactInfo,
    status: validateContactInfoStatus,
    value: validateContactInfoStatusResults,
    errors: validateContactInfoStatusErrors,
  } = useAsync<ContactValidationResult>(validateContactDetails, false);

  const identityVerification = async (partyInfo: ISavePhoneNumberPayload): Promise<IdentityVerificationResult> => {
    const token = await getAccessToken();
    return await verifyIdentity({ ...partyInfo }, token);
  };

  const {
    execute: enhancedIdentityVerification,
    status: enhancedIdentityVerificationStatus,
    value: enhancedIdentityVerificationStatusResults,
    errors: enhancedIdentityVerificationStatusErrors,
  } = useAsync<IdentityVerificationResult>(identityVerification, false);

  useEffect(() => {
    if (
      fetchMarketPlaceStatus === 'error' ||
      updateServiceStatus === 'error' ||
      validateContactInfoStatus === 'error' ||
      enhancedIdentityVerificationStatus === 'error'
    ) {
      const errorMessages = [
        ...(fetchMarketPlaceErrors ?? []),
        ...(updateServiceStatusErrors ?? []),
        ...(validateContactInfoStatusErrors ?? []),
        ...(enhancedIdentityVerificationStatusErrors ?? []),
      ];
      if (errorMessages?.length) {
        errorMessages.map((err) => {
          addGlobalMsg({
            message: err,
            type: 'error',
          });
        });
      }
    }
  }, [fetchMarketPlaceStatus, updateServiceStatus, validateContactInfoStatus, enhancedIdentityVerificationStatus]);

  return {
    isMarketPlaceEnabled,
    fetchMarketPlaceServices,
    fetchMarketPlaceServicesResults,
    fetchMarketPlaceStatus,
    updateMarketPlaceServiceStatus,
    updateServiceStatus,
    doValidateContactInfo,
    validateContactInfoStatus,
    validateContactInfoStatusResults,
    enhancedIdentityVerification,
    enhancedIdentityVerificationStatusResults,
    enhancedIdentityVerificationStatus,
  };
};
